import Link, { LinkProps } from 'next/link'
import React, { forwardRef } from 'react'
import styled, { css } from 'styled-components'
import { media } from 'utils/media'
import { LoadingCircle } from 'lib/LoadingCircle'
import { textSize, textWeight, Text } from 'lib/Text'
import { TooltipText } from 'lib/TooltipText'

export const colors = ['primary', 'secondary', 'green'] as const
export type ButtonColor = (typeof colors)[number]

export const variants = ['normal', 'ghost'] as const
export type ButtonVariant = (typeof variants)[number]

export const sizes = ['normal', 'small', 'tiny'] as const
export type ButtonSize = (typeof sizes)[number]

const baseStyle = css`
  ${textSize('ps')}
  ${textWeight('semi')}
  align-items: center;
  background: white;
  border-radius: var(--button-radius);
  border: 1px solid var(--neutral-20);
  cursor: pointer;
  display: inline-flex;
  justify-content: center;
  margin: 0;
  min-height: 48px;
  padding: 10px 16px;
  position: relative;
  transition: padding-right 0.1s ease-in-out, background 0.1s ease-in, border-color 0.1s ease-in, box-shadow 0.15s ease-in;
  will-change: border-color, box-shadow;

  &:active,
  &:active:enabled {
    transform: scale(1.02);
  }

  &:hover:disabled {
    cursor: not-allowed;
  }
`

const roundStyle = css`
  border-radius: 100px;
`

const smallSizeStyle = css`
  ${textSize('pxs')}
  min-height: 40px;
  padding: 8px 12px;
`

const tinySizeStyle = css`
  ${textSize('small')}
  ${textWeight('bold')}
  line-height: 12px;
  min-height: 32px;
  padding: 4px 12px;
`

const primaryStyle = css`
  background-color: var(--neutral-100);
  border-color: var(--neutral-100);
  color: white;
  &:hover,
  &:hover:enabled {
    border-color: var(--neutral-80);
    background-color: var(--neutral-80);
    color: white;
  }
  &:active,
  &:active:enabled {
    background-color: var(--neutral-100);
    border-color: var(--neutral-100);
    color: white;
  }
  &:disabled {
    color: var(--neutral-40);
    background-color: var(--neutral-10);
    border-color: var(--neutral-10);
  }
`

const secondaryStyle = css`
  background-color: white;
  box-shadow: 0 0 0 0 var(--neutral-80);
  color: var(--neutral-100);
  border-color: var(--neutral-20);
  &:hover,
  &:hover:enabled {
    border-color: #ccc;
  }
  &:active,
  &:active:enabled {
    background-color: #f8f8f8;
    color: var(--neutral-100);
  }
  &:disabled {
    color: var(--neutral-30);
  }
`

const greenStyle = css`
  background-color: var(--green-80);
  border-color: var(--green-80);
  color: white;
  &:hover,
  &:hover:enabled {
    background-color: var(--green-60);
    border-color: var(--green-60);
    color: white;
  }
  &:active,
  &:active:enabled {
    background-color: var(--neutral-20);
    color: var(--neutral-100);
  }

  path {
    fill: white;
  }
`

const ghostStyle = css`
  background: none;
  color: var(--neutral-100);
  border: 1px solid transparent;
  &:hover:enabled {
    background-color: var(--neutral-10);
    border: 1px solid transparent;
    color: var(--neutral-100);
  }
  &:active:enabled {
    background-color: var(--neutral-20);
  }
  &:disabled {
    color: var(--neutral-30);
  }
`

const secondaryActiveStyle = css`
  background-color: #f8f8f8;
  border-color: var(--neutral-80);
  box-shadow: inset 0 0 0px 1px var(--neutral-80);
  &:hover:enabled,
  &:hover {
    background-color: #f8f8f8;
    border-color: var(--neutral-80);
  }
`

const primaryActiveStyle = css`
  background-color: var(--neutral-100);
  border-color: var(--neutral-80);
  box-shadow: inset 0 0 0px 1px var(--neutral-80);
  &:hover:enabled,
  &:hover {
    background-color: var(--neutral-100);
    border-color: var(--neutral-80);
  }
`

const ghostActiveStyle = css`
  border: 1px solid transparent;
  box-shadow: none;
  &:hover:enabled,
  &:hover {
    border: 1px solid transparent;
  }
`

const selectedStyle = css`
  border-color: var(--neutral-80);
  box-shadow: inset 0 0 0px 1px var(--neutral-80);

  &:hover:enabled,
  &:hover {
    border-color: var(--neutral-80);
  }

  path {
    fill: black;
  }
`

interface StyleProps {
  active?: boolean
  disabled?: boolean
  hasLabel?: boolean
  isLoading?: boolean
  renderIconOnMobile: boolean
  round?: boolean
  selected?: boolean
  showIcon?: boolean
  startIcon?: React.ReactNode
  endIcon?: React.ReactNode
  size?: ButtonSize
  variant?: ButtonVariant
}

const HTMLButton = styled.button<StyleProps>`
  ${baseStyle}

  ${(props) =>
    props.disabled &&
    css`
      opacity: 0.5;
    `}

  svg {
    width: 16px;
    margin-right: ${(props) => (props.hasLabel && props.startIcon ? '12px' : '0')};
    margin-left: ${(props) => (props.hasLabel && props.endIcon ? '12px' : '0')};
    transition: opacity 0.1s ease-in-out;
    opacity: ${(props) => (props.showIcon ? '1' : '0')};
  }

  ${(props) => props.size === 'small' && smallSizeStyle}
  ${(props) => props.size === 'tiny' && tinySizeStyle}
  ${(props) => props.color === 'primary' && primaryStyle}
  ${(props) => props.color === 'secondary' && secondaryStyle}
  ${(props) => props.color === 'green' && greenStyle}
  ${(props) => props.variant === 'ghost' && ghostStyle}
  ${(props) => props.round && roundStyle}
  ${(props) => props.active && props.color === 'secondary' && secondaryActiveStyle}
  ${(props) => props.active && props.color === 'primary' && primaryActiveStyle}
  ${(props) => props.active && props.variant === 'ghost' && ghostActiveStyle}
  ${(props) => props.selected && selectedStyle}

  ${(props) =>
    !props.renderIconOnMobile &&
    css`
      ${media.phoneDown} {
        svg {
          display: none;
        }
      }
    `}

    ${(props) =>
    !props.endIcon &&
    props.isLoading &&
    css`
      padding-right: 42px;
    `}
`

const LoadingContainer = styled.div<{ isLoading: boolean }>`
  position: absolute;
  right: 12px;
  display: none;
  opacity: 0;
  transition: opacity 0.1s ease-in-out;

  ${(props) =>
    props.isLoading &&
    css`
      display: block;
      opacity: 1;
    `}
`

const SecondaryLabel = styled(Text)<{ buttonSize: ButtonSize }>`
  margin-left: 8px;
  ${(props) => props.buttonSize === 'normal' && textSize('ps')};
  ${(props) => props.buttonSize === 'small' && textSize('pxs')};
  ${(props) => props.buttonSize === 'small' && textSize('small')};
`

export interface Props<ElementType = HTMLButtonElement> {
  active?: boolean
  ariaLabel?: string
  className?: string
  color?: ButtonColor
  disabled?: boolean
  form?: string
  startIcon?: React.ReactNode
  endIcon?: React.ReactNode
  label?: string | React.ReactNode
  labelAsTooltip?: boolean
  loading?: boolean
  loadingIcon?: React.ReactNode
  loadingLabel?: string | React.ReactNode
  onClick?: React.MouseEventHandler<ElementType>
  onMouseDown?: React.MouseEventHandler<ElementType>
  onMouseUp?: React.MouseEventHandler<ElementType>
  onMouseLeave?: React.MouseEventHandler<ElementType>
  onTouchStart?: React.TouchEventHandler<ElementType>
  onTouchEnd?: React.TouchEventHandler<ElementType>
  renderIconOnMobile?: boolean
  round?: boolean
  secondaryLabel?: string | React.ReactNode
  selected?: boolean
  size?: ButtonSize
  type?: 'button' | 'submit'
  variant?: ButtonVariant
}

interface MaybeTooltipProps {
  label: string | React.ReactNode
  children: React.ReactElement
  labelAsTooltip: boolean
}
const MaybeTooltip = ({ label, children, labelAsTooltip }: MaybeTooltipProps) => {
  if (labelAsTooltip) {
    return <TooltipText content={label}>{children}</TooltipText>
  }

  return <>{children}</>
}

const AnchorButton = styled.a<Props>`
  ${baseStyle}
  text-decoration: none;

  ${(props) => props.size === 'small' && smallSizeStyle}
  ${(props) => props.size === 'tiny' && tinySizeStyle}
  ${(props) => props.color === 'primary' && primaryStyle}
  ${(props) => props.color === 'secondary' && secondaryStyle}
  ${(props) => props.color === 'green' && greenStyle}
  ${(props) => props.round && roundStyle}
  ${(props) => props.variant === 'ghost' && ghostStyle}
`

export const Button = forwardRef((props: Props, ref: React.Ref<HTMLButtonElement>) => {
  const {
    active = false,
    ariaLabel,
    className,
    color = 'secondary',
    disabled = false,
    form,
    startIcon,
    endIcon,
    label,
    labelAsTooltip = false,
    loading,
    loadingIcon = <LoadingCircle variant="dark" />,
    loadingLabel,
    onClick,
    onMouseDown,
    onMouseUp,
    onMouseLeave,
    onTouchStart,
    onTouchEnd,
    renderIconOnMobile = true,
    round,
    secondaryLabel,
    selected,
    type = 'button',
    variant = 'normal',
    size = 'normal'
  } = props

  return (
    <MaybeTooltip label={label} labelAsTooltip={labelAsTooltip}>
      <HTMLButton
        active={active}
        aria-label={ariaLabel}
        className={className}
        color={color}
        disabled={disabled}
        endIcon={endIcon}
        form={form}
        hasLabel={!labelAsTooltip && !!label}
        isLoading={loading}
        onClick={onClick}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseLeave}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        ref={ref}
        renderIconOnMobile={renderIconOnMobile}
        round={round}
        selected={selected}
        showIcon={!loading || !endIcon}
        size={size}
        startIcon={startIcon}
        type={type}
        variant={variant}>
        {startIcon}
        <LoadingContainer isLoading={!!loading}>{loading && loadingIcon}</LoadingContainer>
        {!labelAsTooltip && (loading ? loadingLabel ?? label : label)}
        {secondaryLabel && (
          <SecondaryLabel weight="regular" buttonSize={size}>
            {secondaryLabel}
          </SecondaryLabel>
        )}
        {endIcon}
      </HTMLButton>
    </MaybeTooltip>
  )
})

Button.displayName = 'Button'

// Omitting props that aren't suported stylewise yet.
type LinkButtonProps = LinkProps & Omit<Props<HTMLAnchorElement>, keyof StyleProps>

export interface ActionLinkProps extends LinkButtonProps {
  children?: React.ReactNode
  className?: string
  color?: ButtonColor
  onClick?: (e: React.MouseEvent<Element, MouseEvent>) => void
  variant?: ButtonVariant
  size?: ButtonSize
  round?: boolean
  target?: React.HTMLAttributeAnchorTarget
  rel?: string
}

export const LinkButton = forwardRef((props: ActionLinkProps, ref: React.Ref<HTMLButtonElement>) => {
  const { children, className, color = 'secondary', variant = 'normal', size = 'normal', round, onClick, target, rel, ...otherProps } = props

  return (
    <Link legacyBehavior {...otherProps} passHref>
      <AnchorButton target={target} rel={rel} className={className} color={color} variant={variant} onClick={onClick} round={round} size={size}>
        {children}
      </AnchorButton>
    </Link>
  )
})

LinkButton.displayName = 'LinkButton'
